import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Stack, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { capitalize } from 'lodash';
import { ETLScheduledJob } from '~/api/types';
import { useGetScheduledETLJobsQuery } from '~/api/v2/hooks';
import { Table, Tooltip } from '~/components';
import { PAGE_SIZE } from '~/components/Table';
import { ProjectActionTypeEnum, useProjectActionState } from '~/hooks';
import { truncateStyles } from '~/pages/StoragePage/components/styles';
import { useAppDispatch } from '~/redux';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { DATE_FORMAT, TIME_FORMAT } from '~/utils';
import { JobActionsCell } from '.';
import { actionsContainerStyles } from '../../WorkbenchTab/components/styles';
import { actionButtonStyles } from './styles';

const ScheduledJobsTab = () => {
  const projectId = +useParams().projectid!;
  const dispatch = useAppDispatch();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  const {
    data: scheduledJobs,
    isFetching,
    refetch,
  } = useGetScheduledETLJobsQuery({
    projectId,
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
  });
  const { isDisabledAction: isScheduleDisabledByProject, tooltipMessage: scheduleDisabledMessage } =
    useProjectActionState({
      actionType: ProjectActionTypeEnum.ETL_JOB,
    });

  const columns: GridColDef<ETLScheduledJob>[] = [
    {
      field: 'name',
      sortable: false,
      headerName: 'Job Name',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.row.name}>
          <Typography sx={truncateStyles}>{params.row.name || '—'}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'file_name',
      sortable: false,
      headerName: 'Code File',
    },
    {
      field: 'start_date',
      headerName: 'Scheduled Time',
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams) =>
        format(new Date(`${row.start_date}Z`), `${DATE_FORMAT} ${TIME_FORMAT}`),
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      sortable: false,
      flex: 0.7,
      renderCell: ({ row }) => capitalize(row.frequency).replace(/_/g, ' '),
    },
    {
      field: 'actions',
      sortable: false,
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      flex: 0.6,
      renderCell: (params) => <JobActionsCell row={params.row} />,
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Scheduled Jobs</Typography>
        <Box sx={actionsContainerStyles}>
          <CustomButton
            buttonType="outlined"
            disabled={isFetching}
            sx={actionButtonStyles}
            label={isFetching ? 'Updating' : 'Refresh'}
            onClick={() => refetch()}
          />
          <Tooltip disableHoverListener={!isScheduleDisabledByProject} title={scheduleDisabledMessage}>
            <CustomButton
              onClick={() => dispatch(openModal({ type: ModalTypesEnum.ETL_SCHEDULE_JOB }))}
              disabled={isFetching || isScheduleDisabledByProject}
              sx={actionButtonStyles}
              label="Schedule"
            />
          </Tooltip>
        </Box>
      </Stack>
      <Table
        rows={scheduledJobs?.data || []}
        columns={columns}
        loading={isFetching}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        rowCount={scheduledJobs?.pagination.total || 0}
        paginationMode="server"
      />
    </Stack>
  );
};

export default ScheduledJobsTab;
